'use client';

import {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type {ReactNode} from 'react';
import {parseAsString, useQueryState} from 'nuqs';
import {useParams} from '@tanstack/react-router';
import {
  decodeShareParam,
  encodeShareParam,
  SHARE_KEYS,
} from '@features/flow/utils/componentScreenId';

/**
 * Subscription context for useSyncExternalStore pattern.
 *
 * Stores `selectedScreenIds: string[]` — index 0 is implicitly the "active"
 * screen (mirrored to the URL). The remaining ids exist only for in-session
 * multi-select (canvas, sidebar). When the URL changes, only ids[0] is
 * persisted; other ids are ephemeral.
 *
 * Components can subscribe with selectors so they only re-render when the
 * specific slice they care about changes (see useIsActiveScreen,
 * useIsScreenSelected).
 */
export type SetSelectedScreenIds = (
  ids: string[] | ((prev: string[]) => string[]),
) => void;

export const ActiveScreenIdSubscriptionContext = createContext<{
  subscribe: (callback: () => void) => () => void;
  getSnapshot: () => string[];
  setSelectedScreenIds: SetSelectedScreenIds;
}>({
  subscribe: () => () => {},
  getSnapshot: () => EMPTY_IDS,
  setSelectedScreenIds: () => {},
});

// Stable empty-array reference so consumers using useSyncExternalStore don't
// see snapshot identity changes when the selection is empty.
const EMPTY_IDS: string[] = [];

export function ActiveScreenIdProvider({children}: {children: ReactNode}) {
  const [screen, setScreen] = useQueryState('screen', parseAsString);
  const [component, setComponent] = useQueryState('component', parseAsString);
  const [activeScreenIdRaw, setActiveScreenIdRaw] = useQueryState(
    'activeScreenId',
    parseAsString,
  );
  const {id: flowId} = useParams({strict: false});

  const urlActiveScreenId = useMemo(
    () =>
      decodeShareParam(
        {screen, component, activeScreenId: activeScreenIdRaw},
        flowId,
      ),
    [screen, component, activeScreenIdRaw, flowId],
  );

  // Snapshot is always a string[]. Index 0 mirrors the URL.
  const valueRef = useRef<string[]>(
    urlActiveScreenId ? [urlActiveScreenId] : EMPTY_IDS,
  );
  const subscribersRef = useRef(new Set<() => void>());
  // Tracks the active id we last wrote to the URL. Used to ignore the
  // round-trip URL update that follows our own writes (which would
  // otherwise stomp on multi-selection because nuqs is async — when our
  // setter fires, valueRef has [B, A] but the URL is still "A" until
  // nuqs commits, and the layout effect would race-reset us back to [A]).
  const lastWrittenActiveRef = useRef<string | null>(urlActiveScreenId);

  useLayoutEffect(() => {
    const current = valueRef.current;
    const currentActive = current[0] ?? null;
    if (currentActive === urlActiveScreenId) {
      // URL has settled to what we wrote — stop blocking external changes.
      lastWrittenActiveRef.current = null;
      return;
    }

    // If our local state already reflects what we last wrote, the URL is
    // either still catching up (nuqs is async) or this `useLayoutEffect`
    // is firing for a stale `urlActiveScreenId`. Either way, the URL is
    // not the source of truth right now — trust our local state and
    // don't blow away the multi-selection.
    if (currentActive === lastWrittenActiveRef.current) return;

    // URL changed externally (back/forward, share link, etc.) — reset
    // selection to just the URL active id. This matches the "URL is the
    // source of truth for the primary id; multi-select is ephemeral" rule.
    valueRef.current = urlActiveScreenId ? [urlActiveScreenId] : EMPTY_IDS;
    lastWrittenActiveRef.current = urlActiveScreenId;
    subscribersRef.current.forEach((callback) => callback());
  }, [urlActiveScreenId]);

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => {
      // eslint-disable-next-line drizzle/enforce-delete-with-where -- This is Set.delete(), not a database operation
      subscribersRef.current.delete(callback);
    };
  }, []);

  const getSnapshot = useCallback(() => valueRef.current, []);

  // Write the URL to mirror the new primary id. Pulled into a ref so we can
  // hold a stable setSelectedScreenIds identity.
  const writeUrlRef = useRef<(id: string | null) => void>(() => {});
  // eslint-disable-next-line react-hooks/refs
  writeUrlRef.current = (id: string | null) => {
    const next = id === null ? null : encodeShareParam(id);
    const setters = {
      screen: setScreen,
      component: setComponent,
      activeScreenId: setActiveScreenIdRaw,
    };
    for (const key of SHARE_KEYS) {
      void setters[key](next?.key === key ? next.value : null);
    }
  };

  const setSelectedScreenIds = useCallback<SetSelectedScreenIds>((update) => {
    const prev = valueRef.current;
    const next = typeof update === 'function' ? update(prev) : update;

    // Reference equality + same primary + same length + same membership-by-index
    // is enough to skip the update.
    if (next === prev) return;
    if (next.length === prev.length && next.every((id, i) => id === prev[i])) {
      return;
    }

    const nextActive = next[0] ?? null;
    const prevActive = prev[0] ?? null;

    valueRef.current = next.length === 0 ? EMPTY_IDS : next;

    if (nextActive !== prevActive) {
      lastWrittenActiveRef.current = nextActive;
      writeUrlRef.current(nextActive);
    }

    subscribersRef.current.forEach((callback) => callback());
  }, []);

  const subscriptionValue = useRef({
    subscribe,
    getSnapshot,
    setSelectedScreenIds,
  });

  return (
    <ActiveScreenIdSubscriptionContext.Provider
      // eslint-disable-next-line react-hooks/refs
      value={subscriptionValue.current}
    >
      {children}
    </ActiveScreenIdSubscriptionContext.Provider>
  );
}
